<script setup lang="ts">
import type { V2CurrentPositionRow } from '@gpx-spa/queries/src/getAccountCurrentPositionsV2';
import type { ShipmentExceptionRow } from '@gpx-spa/queries/src/shipment-exception-types';
import type { V2ShipmentDetail } from '@gpx-spa/queries/src/getAccountShipmentV2';
import { computed } from 'vue';
import {
  allShipmentDevices,
  originDestinationFromShipment,
} from '@/utils/shipment-detail-stops';
import { openExceptionsForDevice } from '@/utils/shipment-exception-device-join';
import ShipmentExceptionsSummaryCard from '@/components/shipment/detail/cards/ShipmentExceptionsSummaryCard.vue';
import ShipmentOriginDestinationCard from '@/components/shipment/detail/cards/ShipmentOriginDestinationCard.vue';
import ShipmentDeviceCard from '@/components/shipment/detail/cards/ShipmentDeviceCard.vue';
import ShipmentDetailsCard from '@/components/shipment/detail/cards/ShipmentDetailsCard.vue';
import ShipmentNotesCard from '@/components/shipment/detail/cards/ShipmentNotesCard.vue';
import ShipmentVisibilityMetricsCard from '@/components/shipment/detail/cards/ShipmentVisibilityMetricsCard.vue';

const props = defineProps<{
  shipment: V2ShipmentDetail;
  exceptions: ShipmentExceptionRow[];
  devicePositions: V2CurrentPositionRow[];
  positionsLoading: boolean;
}>();

const emit = defineEmits<{
  viewExceptions: [];
}>();

const originDestination = () => originDestinationFromShipment(props.shipment);
const devices = computed(() => allShipmentDevices(props.shipment));

function positionForDevice(deviceId: number): V2CurrentPositionRow | null {
  return (
    props.devicePositions.find((row) => row.device_id === deviceId) ?? null
  );
}

function exceptionsForDevice(deviceId: number) {
  return openExceptionsForDevice(props.exceptions, deviceId);
}
</script>

<template>
  <div class="space-y-4">
    <ShipmentExceptionsSummaryCard
      :exceptions="exceptions"
      @view-exceptions="emit('viewExceptions')"
    />
    <ShipmentOriginDestinationCard :origin-destination="originDestination()" />
    <template v-if="devices.length > 0">
      <ShipmentDeviceCard
        v-for="device in devices"
        :key="device.id"
        :device="device"
        :position="positionForDevice(device.id)"
        :exceptions="exceptionsForDevice(device.id)"
        :is-loading="positionsLoading"
      />
    </template>
    <ShipmentDeviceCard
      v-else
      :device="null"
      :position="null"
      :is-loading="positionsLoading"
    />
    <ShipmentDetailsCard :shipment="shipment" />
    <ShipmentNotesCard :notes="shipment.notes" />
    <ShipmentVisibilityMetricsCard />
  </div>
</template>
